import { motion } from "framer-motion";
import { Check, Clock, Wallet, BellRing, HeartHandshake, Quote } from "lucide-react";
import { useLang } from "@/i18n";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Breakfast() {
  const { t } = useLang();
  const includes = t("bf.includes");
  const info = [
    [Clock, t("bf.info1")],
    [Wallet, t("bf.info2")],
    [BellRing, t("bf.info3")],
    [HeartHandshake, t("bf.info4")],
  ];

  return (
    <section id="mic-dejun" className="py-24 md:py-32 bg-cream-light border-y border-warm-grey/30" data-testid="breakfast-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-5">
              <motion.div {...fadeUp()} className="overflow-hidden rounded-t-[140px] rounded-b-3xl group">
                <img src="/assets/mic-dejun.jpg" alt={t("bf.img_alt")} loading="lazy" data-testid="breakfast-image"
                  className="w-full h-auto group-hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div {...fadeUp(0.15)} className="bg-cream border border-warm-grey/40 rounded-2xl p-6" data-testid="breakfast-quote-1">
                <Quote size={18} className="text-terracotta/60" />
                <p className="mt-3 text-sm text-brown font-light leading-relaxed">„{t("bf.quote1")}"</p>
                <p className="mt-2 text-xs tracking-widest uppercase text-walnut font-semibold">Mari · Google</p>
              </motion.div>
              <motion.div {...fadeUp(0.25)} className="bg-cream border border-warm-grey/40 rounded-2xl p-6" data-testid="breakfast-quote-2">
                <Quote size={18} className="text-terracotta/60" />
                <p className="mt-3 text-sm text-brown font-light leading-relaxed">„{t("bf.quote2")}"</p>
                <p className="mt-2 text-xs tracking-widest uppercase text-walnut font-semibold">Alexandru · Google</p>
              </motion.div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <motion.p {...fadeUp()} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-terracotta" />
              {t("bf.eyebrow")}
            </motion.p>
            <motion.h2 {...fadeUp(0.1)} className="mt-5 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main" data-testid="breakfast-title">
              {t("bf.title")}
            </motion.h2>
            <motion.p {...fadeUp(0.2)} className="mt-7 text-base md:text-lg text-brown font-light leading-relaxed max-w-2xl">
              {t("bf.intro")}
            </motion.p>

            <motion.div {...fadeUp(0.3)} className="mt-10">
              <h3 className="font-serif text-2xl text-main">{t("bf.includes_title")}</h3>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                {includes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-brown/90 font-light">
                    <Check size={16} className="text-green-dark mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.p {...fadeUp(0.35)} className="mt-9 text-sm md:text-base text-brown/80 font-light leading-relaxed max-w-2xl">
              {t("bf.philosophy")}
            </motion.p>
            <motion.p {...fadeUp(0.4)} className="mt-4 text-sm md:text-base text-brown/80 font-light leading-relaxed max-w-2xl">
              {t("bf.serving")}
            </motion.p>

            <motion.div {...fadeUp(0.45)} className="mt-9 flex flex-wrap gap-3" data-testid="breakfast-info">
              {info.map(([Icon, text]) => (
                <div key={text} className="flex items-center gap-2.5 bg-cream border border-warm-grey/50 rounded-full px-4 py-2.5">
                  <Icon size={15} className="text-terracotta shrink-0" />
                  <span className="text-sm text-main font-medium">{text}</span>
                </div>
              ))}
            </motion.div>

            <motion.p {...fadeUp(0.5)} className="mt-10 font-serif italic text-xl md:text-2xl text-green-dark max-w-xl" data-testid="breakfast-final">
              „{t("bf.final")}"
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
}
